import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Check, Shield } from "lucide-react";
import { api } from "@/lib/api";

interface DamageReportDialogProps {
	open: boolean;
	onClose: () => void;
	bookingId: string;
}

export function DamageReportDialog({
	open,
	onClose,
	bookingId,
}: DamageReportDialogProps) {
	const [description, setDescription] = useState("");
	const [severity, setSeverity] = useState<"minor" | "moderate" | "severe">("minor");
	const [depositDeduction, setDepositDeduction] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [step, setStep] = useState(1);

	const severityInfo = {
		minor: {
			label: "Minor",
			description: "Small scratches, scuffs, or cosmetic issues",
			color: "bg-yellow-50 text-yellow-700 border-yellow-200",
			suggestedDeduction: "10-20%",
		},
		moderate: {
			label: "Moderate",
			description: "Functional impairment, missing parts, or significant wear",
			color: "bg-orange-50 text-orange-700 border-orange-200",
			suggestedDeduction: "30-60%",
		},
		severe: {
			label: "Severe",
			description: "Tool broken, major damage, or safety concern",
			color: "bg-red-50 text-red-700 border-red-200",
			suggestedDeduction: "70-100%",
		},
	};

	async function handleSubmit() {
		setSubmitting(true);
		try {
			await api.createDamageReport({
				bookingId,
				description,
				severity,
				depositDeduction: Number(depositDeduction) || 0,
			});
			setSubmitted(true);
			setTimeout(() => {
				onClose();
				setSubmitted(false);
				setDescription("");
				setSeverity("minor");
				setDepositDeduction("");
				setStep(1);
			}, 2000);
		} catch (e) {
			console.error("Failed to submit damage report:", e);
		} finally {
			setSubmitting(false);
		}
	}

	function reset() {
		setDescription("");
		setSeverity("minor");
		setDepositDeduction("");
		setStep(1);
		setSubmitted(false);
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) {
					onClose();
					reset();
				}
			}}
		>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{submitted ? (
							<>
								<Check className="w-5 h-5 text-emerald-600" />
								Report Submitted
							</>
						) : (
							<>
								<AlertTriangle className="w-5 h-5 text-orange-500" />
								Report Damage
							</>
						)}
					</DialogTitle>
					<DialogDescription>
						{submitted
							? "The borrower has been notified. The deposit will be held until resolution."
							: step === 1
								? "Step 1: Describe the damage to the tool"
								: "Step 2: Specify deposit deduction"}
					</DialogDescription>
				</DialogHeader>

				{submitted ? (
					<div className="text-center py-6">
						<div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
							<Check className="w-6 h-6 text-emerald-600" />
						</div>
						<p className="text-sm text-muted-foreground">
							We'll review the report and mediate if needed.
						</p>
					</div>
				) : step === 1 ? (
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>Severity</Label>
							<div className="grid grid-cols-3 gap-2">
								{(Object.entries(severityInfo) as [typeof severity, typeof severityInfo.minor][]).map(
									([key, info]) => (
										<button
											key={key}
											onClick={() => setSeverity(key)}
											className={`p-3 rounded-lg border text-center transition-all ${
												severity === key
													? info.color + " border-2"
													: "border-border hover:border-muted-foreground/30"
											}`}
										>
											<p className="text-sm font-medium">{info.label}</p>
											<p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
												{info.description}
											</p>
										</button>
									)
								)}
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="damage-desc">Description</Label>
							<Textarea
								id="damage-desc"
								placeholder="Describe the damage in detail. What happened? When did you notice it?"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								rows={4}
							/>
						</div>

						<div className="flex gap-2">
							<Button variant="outline" className="flex-1" onClick={onClose}>
								Cancel
							</Button>
							<Button
								className="flex-1"
								disabled={!description}
								onClick={() => setStep(2)}
							>
								Next
							</Button>
						</div>
					</div>
				) : (
					<div className="space-y-4">
						<div className="p-3 bg-muted/50 rounded-lg space-y-1">
							<div className="flex items-center justify-between">
								<span className="text-sm font-medium">Severity</span>
								<Badge
									variant="outline"
									className={severityInfo[severity].color}
								>
									{severityInfo[severity].label}
								</Badge>
							</div>
							<p className="text-xs text-muted-foreground line-clamp-2">
								{description}
							</p>
						</div>

						<div className="space-y-2">
							<Label htmlFor="deduction">
								Deposit deduction amount ($)
							</Label>
							<Input
								id="deduction"
								type="number"
								min="0"
								placeholder="0"
								value={depositDeduction}
								onChange={(e) => setDepositDeduction(e.target.value)}
							/>
							<p className="text-xs text-muted-foreground flex items-center gap-1">
								<Shield className="w-3 h-3" />
								Suggested: {severityInfo[severity].suggestedDeduction} of deposit
							</p>
						</div>

						<div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
							<p className="text-xs text-orange-800">
								The borrower will be notified and given a chance to respond.
								If disputed, our mediation team will review the case.
							</p>
						</div>

						<div className="flex gap-2">
							<Button
								variant="outline"
								className="flex-1"
								onClick={() => setStep(1)}
							>
								Back
							</Button>
							<Button
								className="flex-1"
								onClick={handleSubmit}
								disabled={submitting}
							>
								{submitting ? "Submitting..." : "Submit Report"}
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
